import {
  useContext,
  useState,
  useEffect,
} from 'react';

import {
  googlePayment,
  Client,
  GooglePayment as BraintreeGooglePayment,
} from 'braintree-web';

import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { ProvidersLibsContext } from '@/services/providersLibs/providersLibsContext';
import { usePaymentsConfig } from '@/services/paymentsConfig/usePaymentsConfig';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';

const initBraintreeGooglePayment = async (client: Client, googleMerchantId: string): Promise<BraintreeGooglePayment> => (
  googlePayment.create({
    client,
    googlePayVersion: 2,
    googleMerchantId,
  })
);

// API reference: https://developers.google.com/pay/api/web/reference/request-objects#PaymentDataRequest
const buildPaymentRequest = async (
  braintreeGooglePayment: BraintreeGooglePayment,
  paymentSelectState: PaymentSelectState,
): Promise<google.payments.api.PaymentDataRequest> => (
  {
    ...await braintreeGooglePayment.createPaymentDataRequest({
      transactionInfo: {
        totalPriceStatus: 'FINAL',
        totalPrice: paymentSelectState.amount.toString(),
        currencyCode: paymentSelectState.currency,
      },
    }),
    emailRequired: true,
    shippingAddressRequired: true,
    // TODO: Check to make sure AMEX is not permittted while it's not working with Braintree
    // We'll need to set allowedCardNetworks if it is
  }
);

export function useBraintreeGooglepayInit() {
  const paymentSelectState = useAppSelector(state => state.paymentSelect);

  const providersLibs = useContext(ProvidersLibsContext);

  const { getBraintreeGooglepayMode, getBraintreeGooglepayMerchantId } = usePaymentsConfig();

  const [environment, setEnvironment] = useState <'PRODUCTION' | 'TEST'>();
  const [braintreeGooglePayment, setBraintreeGooglePayment] = useState<BraintreeGooglePayment>();
  const [paymentRequest, setPaymentRequest] = useState<google.payments.api.PaymentDataRequest>();

  const googlepayMode = getBraintreeGooglepayMode();
  const googlepayMerchantId = getBraintreeGooglepayMerchantId();

  // Init environment on component load
  useEffect(() => {
    // Wait for googlepayMode
    if (!googlepayMode) {
      return;
    }

    const newEnvironment = (googlepayMode === 'live') ? 'PRODUCTION' : 'TEST';
    setEnvironment(newEnvironment);
  }, [googlepayMode]);

  // Load Braintree braintreeGooglePayment instance on component load
  useEffect(() => {
    async function loadBraintree() {
      // Wait until Braintree client & environment are available before loading braintreeGooglePayment
      if (
        !providersLibs?.braintreeClient
        || !environment
      ) {
        return;
      }

      // Wait for googlepayMerchantId *only* if we're in PRODUCTION environment
      if (environment === 'PRODUCTION' && !googlepayMerchantId) {
        return;
      }

      if (!braintreeGooglePayment) {
        try {
          const braintreeGooglePaymentInstance = await initBraintreeGooglePayment(providersLibs.braintreeClient, googlepayMerchantId ?? 'TEST');
          setBraintreeGooglePayment(braintreeGooglePaymentInstance);
        } catch (error) {
          logger.error('Error initialising Braintree braintreeGooglePayment');
          logger.error(error);
        }
      }
    }
    void loadBraintree();
  }, [
    providersLibs?.braintreeClient,
    environment,
    googlepayMerchantId,
    braintreeGooglePayment,
  ]);

  // Init paymentRequest on component load
  useEffect(() => {
    async function initPaymentRequest() {
      // Wait until Braintree braintreeGooglePayment is available before initalising paymentRequest
      if (!braintreeGooglePayment) {
        return;
      }

      if (!paymentRequest) {
        try {
          const paymentRequestInstance = await buildPaymentRequest(braintreeGooglePayment, paymentSelectState);
          setPaymentRequest(paymentRequestInstance);
        } catch (error) {
          logger.error('Error initialising Googlepay paymentRequest');
          logger.error(error);
        }
      }
    }
    void initPaymentRequest();
  }, [braintreeGooglePayment, paymentRequest, paymentSelectState]);

  return {
    braintreeGooglePayment,
    environment,
    paymentRequest,
  };
}
